import { useAppDispatch, useAppSelector } from '@gym-redux/store';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Table, { TableProps } from '@gym-molecules/Table/Table';
import { useEffect, useRef, useState } from 'react';
import Loader from '@gym-atoms/Loader/Loader';
import Button, { ButtonSizes, ButtonVariants } from '@gym-atoms/Button/Button';
import DialogBox, { DialogBoxVariants } from '@gym-atoms/Dialog/DialogBox';
import Text from '@gym-atoms/Text/Text';
import {
  GymChainAccessCategoryTable,
  GymChainAccessCategoryType
} from '../../../particles/types/GymChainAccessCategory';
import {
  deleteGymChainAccessCategory,
  fetchGymChainAccessCategory
} from '../../../redux/slices/gymChainAccessCategorySlice';
export const MembershipAccessCategoryListTable = (
  props: GymChainsMembershipAccessCategoryListTableProps
) => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const [selectedRow, setSelectedRow] = useState<GymChainAccessCategoryTable>();
  const [selectedRows, setSelectedRows] = useState<Array<GymChainAccessCategoryTable>>([]);
  const gymChainId = props.gymChainId;
  const { t } = useTranslation();
  const [gymAccessCategorySortField, setGymAccessCategorySortField] = useState('id');
  const [gymAccessCategorySortOrder, setGymAccessCategorySortOrder] = useState(-1);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const isGymAccessCategoryLoading = useAppSelector(
    (state) => state.gymChainAccessCategories.isAccessCategoriesLoading
  );
  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const totalRecords =
    useAppSelector(
      (state) =>
        state.gymChainAccessCategories.items.find((g) => g.siteId === gymChainId)?.totalRecords
    ) || 0;

  const gymChainName = useAppSelector(
    (state) => state.gymChain.items.find((g) => g.id === +gymChainId)?.name
  );
  const currentUser = useAppSelector((state) => state.user);
  useEffect(() => {
    dispatch(
      fetchGymChainAccessCategory({
        siteId: props.gymChainId
      })
    );
  }, []);

  const mapAccessCategories = (accessCategories?: GymChainAccessCategoryType[]) => {
    if (!accessCategories) {
      return;
    }
    const mappedAccessCategories: GymChainAccessCategoryTable[] = [];

    accessCategories.forEach((accessCategory) => {
      const index = mappedAccessCategories.findIndex((mappedAccessCategory) => {
        return mappedAccessCategory.id === accessCategory.accessCategoryId;
      });

      if (index > -1) {
        mappedAccessCategories[index].accessCategoryDetails.push({
          siteLocationAccessCategoryId:
            accessCategory.siteLocationAccessCategoryId > 0
              ? accessCategory.siteLocationAccessCategoryId
              : null,
          siteLocationId: accessCategory.siteLocationId > 0 ? accessCategory.siteLocationId : null,
          siteLocationMembershipId:
            accessCategory.siteLocationMembershipId > 0
              ? accessCategory.siteLocationMembershipId
              : null,
          accessCategoryId:
            accessCategory.accessCategoryId > 0 ? accessCategory.accessCategoryId : null,
          locationName: accessCategory.locationName !== '' ? accessCategory.locationName : null,
          arxAccessCategoryId:
            accessCategory.arxAccessCategoryId !== '' ? accessCategory.arxAccessCategoryId : null,
          membershipName:
            accessCategory.membershipName !== '' ? accessCategory.membershipName : null,
          membershipImportedId:
            accessCategory.membershipImportedId !== '' ? accessCategory.membershipImportedId : null,
          program: accessCategory.program !== '' ? accessCategory.program : null,
          scheduleType: accessCategory.scheduleType !== '' ? accessCategory.scheduleType : null
        });
      } else {
        mappedAccessCategories.push({
          id: accessCategory.id,
          siteId: accessCategory.siteId,
          name: accessCategory.name,
          gender: accessCategory.gender,
          age: accessCategory.age > 0 ? accessCategory.age : null,
          schedulesOnly: accessCategory.schedulesOnly,
          status: accessCategory.status,
          accessCategoryDetails: [
            {
              siteLocationAccessCategoryId:
                accessCategory.siteLocationAccessCategoryId > 0
                  ? accessCategory.siteLocationAccessCategoryId
                  : null,
              siteLocationId:
                accessCategory.siteLocationId > 0 ? accessCategory.siteLocationId : null,
              siteLocationMembershipId:
                accessCategory.siteLocationMembershipId > 0
                  ? accessCategory.siteLocationMembershipId
                  : null,
              accessCategoryId:
                accessCategory.accessCategoryId > 0 ? accessCategory.accessCategoryId : null,
              locationName: accessCategory.locationName !== '' ? accessCategory.locationName : null,
              arxAccessCategoryId:
                accessCategory.arxAccessCategoryId !== ''
                  ? accessCategory.arxAccessCategoryId
                  : null,
              membershipName:
                accessCategory.membershipName !== '' ? accessCategory.membershipName : null,
              membershipImportedId:
                accessCategory.membershipImportedId !== ''
                  ? accessCategory.membershipImportedId
                  : null,
              program: accessCategory.program !== '' ? accessCategory.program : null,
              scheduleType: accessCategory.scheduleType !== '' ? accessCategory.scheduleType : null
            }
          ]
        });
      }
    });

    return mappedAccessCategories;
  };

  const gymChainAccessCategories = useAppSelector((state) =>
    mapAccessCategories(
      state.gymChainAccessCategories.items.find((e) => e.siteId === props.gymChainId)?.items
    )
  );

  const deleteDialogHandler = () => {
    selectedRows && dispatch(deleteGymChainAccessCategory(selectedRows.map((row) => row.id)));

    setDeleteDialogVisible(false);
  };

  const DeleteDialogFooter = () => {
    return (
      <div>
        <Button
          label={t('COMMON.CANCEL')}
          variant={ButtonVariants.textonly}
          onClick={() => setDeleteDialogVisible(false)}
        />
        <Button
          label={t('COMMON.DELETE')}
          variant={ButtonVariants.danger}
          onClick={deleteDialogHandler}
        />
      </div>
    );
  };

  const tablePropsAccessCategory: TableProps<GymChainAccessCategoryTable> = {
    onChildRowClick: (e) => {
      console.log({ e });
      history.push(
        `/gymChains/${gymChainId}/gyms/${e['siteLocationId']}/gymAccessCategories/${e['siteLocationAccessCategoryId']}`
      );
    },
    selectionMode: 'checkbox',
    enableChildRowClick: true,
    exportFileName: `GymAccessCategories-${gymChainName}`,
    data: gymChainAccessCategories || undefined,
    searchPlaceholderText: t('ACCESS_CATEGORY.SEARCH_FIELD_TEXT'),
    excelBtntext: t('ACCESS_CATEGORY.EXPORT_EXCEL_BUTTON_LABEL'),
    totalRecords: totalRecords,
    selectedRows: selectedRows,
    selectedRow: selectedRow,
    pageSize: pageSize,
    offset: offset,
    setPageSize: setPageSize,
    setOffset: setOffset,
    setSelectedRow: setSelectedRow,
    setSelectedRows: setSelectedRows,
    setSortField: setGymAccessCategorySortField,
    setSortOrder: setGymAccessCategorySortOrder,
    sortField: gymAccessCategorySortField,
    sortOrder: gymAccessCategorySortOrder,
    emptyStateTexts: [t('ACCESS_CATEGORY.EMPTY_STATE_TEXT')],
    columns: [
      {
        field: 'name',
        header: t('ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_NAME'),
        toggable: false,
        sortable: true,
        truncateText: true
      },
      {
        field: 'gender',
        header: t('ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_GENDER'),
        toggable: true,
        truncateText: true
      },
      {
        field: 'age',
        header: t('ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_AGE'),
        toggable: true,
        truncateText: true
      },
      {
        field: 'schedulesOnly',
        header: t('ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_SCHEDULES_ONLY'),
        toggable: true,
        truncateText: true
      },
      {
        field: 'status',
        header: t('ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_STATUS'),
        toggable: true,
        truncateText: true
      }
    ],
    isContextMenu: true,
    menuItem: [
      {
        label: t('COMMON.MODIFY'),
        command: () =>
          history.push(`/gymChains/${gymChainId}/gymChainAccessCategories/${selectedRow?.id}`)
      },
      {
        label: t('COMMON.DELETE'),
        command: () => {
          selectedRows.length = 0;
          selectedRows.push(selectedRow);
          setDeleteDialogVisible(true);
        }
      }
    ],
    lazy: false,
    expandable: true,
    expandableField: 'accessCategoryDetails',
    expandedCols: [
      {
        field: 'locationName',
        header: t('ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_LOCATION_NAME'),
        toggable: true,
        truncateText: true
      },
      {
        field: 'arxAccessCategoryId',
        header: t('ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_ARX_ID'),
        toggable: true,
        truncateText: true
      },
      {
        field: 'membershipImportedId',
        header: t('ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_MEMBERSHIP_IMPORTED_ID'),
        toggable: true,
        truncateText: true
      },
      {
        field: 'siteLocationMembershipId',
        header: t('ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_LOCATION_MEMBERSHIP_ID'),
        toggable: true,
        truncateText: true
      },
      {
        field: 'membershipName',
        header: t('ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_MEMBERSHIP_NAME'),
        toggable: true,
        truncateText: true
      },
      {
        field: 'program',
        header: t('ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_PROGRAM'),
        toggable: true,
        truncateText: true
      },
      {
        field: 'scheduleType',
        header: t('ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_SCHEDULE_TYPE'),
        toggable: true,
        truncateText: true
      }
    ],
    headerBtns: [
      <div className="d-flex" key={1}>
        <Button
          label={t('ACCESS_CATEGORY.ADD_NEW_ACCESS_CATEGORY_BUTTON_LABEL')}
          size={ButtonSizes.small}
          onClick={() => history.push(`/gymChains/${gymChainId}/gymChainAccessCategories/new`)}
        />
      </div>,
      <div className="d-flex" key={2}>
        <Button
          data-cy={'access-categoryTable -table-btn-inactive-beacons'}
          variant={ButtonVariants.outline}
          label={t('ACCESS_CATEGORY.DELETE_SELECTED_ACCESS_CATEGORY_BUTTON_LABEL')}
          size={ButtonSizes.small}
          onClick={() => {
            setDeleteDialogVisible(true);
          }}
          disabled={selectedRows?.length === 0 || false}
        />
      </div>
    ]
  };
  return (
    <div>
      <DialogBox
        dialogVisible={deleteDialogVisible}
        variant={DialogBoxVariants.long}
        dialogDismissableMask={true}
        onHideCallback={() => setDeleteDialogVisible(false)}
        dialogFooter={<DeleteDialogFooter />}
        dialogClosable={false}
        dialogHeader={t('COMMON.DELETE')}
        content={
          <Text>
            {t('ACCESS_CATEGORY.DELETE_MODAL_CONFIRM')}{' '}
            <span className="fw-bold">{selectedRows?.map((row) => row.name).join(', ')}</span>?
          </Text>
        }
      />
      {isGymAccessCategoryLoading ? (
        <Loader shape="table" />
      ) : (
        <Table {...tablePropsAccessCategory} />
      )}
    </div>
  );
};
interface GymChainsMembershipAccessCategoryListTableProps {
  gymChainId: number;
}
